import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Link } from "react-router-dom";
import { 
  Users, Search, RotateCcw, Loader2, Download, FileText, Package, 
  MapPin, Layers, UserCheck, X, CheckSquare, Square, 
  Activity, HeartHandshake, ListOrdered
} from "lucide-react";

import { useFetchStates, useFetchEducationDistricts, useFetchBlocks } from "../../hooks/useJurisData";
import Breadcrumbs from "../../components/Breadcrumbs/Breadcrumbs";
import Logo from "../../assets/RCF-PP.jpg"; 

const API_BASE = process.env.REACT_APP_BACKEND_API_URL;

const ALL_COLUMNS = [
  { key: "sl_no", label: "SL NO" },
  { key: "enr_id", label: "ENROLLMENT NO" },
  { key: "student_name", label: "STUDENT NAME" },
  { key: "nmms_reg_number", label: "NMMS REG NUMBER" },
  { key: "state", label: "STATE" },
  { key: "district", label: "DISTRICT" },
  { key: "nmms_block", label: "NMMS BLOCK" },
  { key: "medium", label: "MEDIUM" },
  { key: "active_yn", label: "ACTIVE YN" },
  { key: "pincode", label: "PINCODE" },
  { key: "village", label: "VILLAGE" },
  { key: "father_name", label: "FATHER NAME" },
  { key: "father_occupation", label: "FATHER OCCUPATION" },
  { key: "mother_name", label: "MOTHER NAME" },
  { key: "mother_occupation", label: "MOTHER OCCUPATION" },
  { key: "gender", label: "GENDER" },
  { key: "cohort_name", label: "COHORT  NAME" },
  { key: "batch_name", label: "BATCH NAME" },
  { key: "parent_email", label: "PARENT EMAIL" },
  { key: "home_address", label: "HOME ADDRESS" },
  { key: "contactno1", label: "STUDENT CONTACT" },
  { key: "contactno2", label: "PARENT CONTACT" },
  { key: "sim_name", label: "SIM NAME" },
  { key: "recharge_status", label: "RECHARGE STATUS" },
  { key: "current_institute_dise_code", label: "CURRENT SCHOOL DISE" },
  { key: "current_institute_name", label: "CURRENT SCHOOL NAME" },
  { key: "previous_institute_dise_code", label: "PREVIOUS SCHOOL DISE" },
  { key: "previous_institute_name", label: "PREVIOUS SCHOOL NAME" },
  { key: "teacher_name", label: "SCHOOL TEACHER NAME" },
  { key: "teacher_mobile", label: "SCHOOL TEACHER PHONE NUMBER" },
  { key: "spl_health_cond_dtls", label: "STUDENT HEALTH CONDITION" },
  { key: "spl_family_cond_dtls", label: "STUDENT FAMILY CONDITION" },
  { key: "applicant_id", label: "STUDENT APPLICATION ID" },
];

export default function Students() {
  const currentPath = ["Admin", "Academics", "Students"];

  const initialFormData = {
    enr_id: "",
    student_name: "",
    batch_id: "",
    cohort_id: "",
    gender: "",
    state_id: "",
    district_id: "",
    block_id: "",
    active_yn: "",
    spl_health_cond: "",
    spl_family_cond: "",
  };

  const [formData, setFormData] = useState(initialFormData);
  const [cohorts, setCohorts] = useState([]);
  const [batches, setBatches] = useState([]);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [blocks, setBlocks] = useState([]);
  
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  
  // Export State & Sequence Tracking
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState(ALL_COLUMNS.map(c => c.key));
  const [columnSearch, setColumnSearch] = useState("");
  
  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const itemsPerPage = 30; 

  useFetchStates(setStates);
  useFetchEducationDistricts(formData.state_id, setDistricts);
  useFetchBlocks(formData.district_id, setBlocks);

  useEffect(() => {
    axios.get(`${API_BASE}/api/cohorts`)
      .then(res => setCohorts(res.data.data || res.data))
      .catch(err => console.error("Cohort fetch error", err));
  }, []);

  useEffect(() => {
    if (!formData.cohort_id) {
      setBatches([]);
      return;
    }
    axios.get(`${API_BASE}/api/batches/cohort/${formData.cohort_id}`)
      .then(res => setBatches(res.data.data || res.data))
      .catch(err => console.error("Batch fetch error", err));
  }, [formData.cohort_id]);

  const fetchStudents = useCallback(async (page = 1) => {
    setLoading(true);
    const offset = (page - 1) * itemsPerPage;

    const apiPayload = {
      enr_id: formData.enr_id,
      name: formData.student_name,
      cohort_number: formData.cohort_id,
      batch_id: formData.batch_id,
      gender: formData.gender,
      state_id: formData.state_id,
      district_id: formData.district_id,
      block_id: formData.block_id,
      active_yn: formData.active_yn,
      spl_health_cond: formData.spl_health_cond,
      spl_family_cond: formData.spl_family_cond,
      limit: itemsPerPage,
      offset: offset
    };

    const params = Object.fromEntries(Object.entries(apiPayload).filter(([_, v]) => v !== "" && v != null));

    try {
      const res = await axios.get(`${API_BASE}/api/search-students`, { params });
      setResults(res.data.data || []);
      setTotalRecords(res.data.pagination?.total || 0);
    } catch (err) {
      console.error("Search failed", err);
      alert("An error occurred during search.");
    } finally {
      setLoading(false);
    }
  }, [formData]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchStudents(1);
  };

  const handleReset = () => {
    setFormData(initialFormData);
    setResults([]);
    setCurrentPage(1);
    setTotalRecords(0);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    fetchStudents(newPage);
  };

  const toggleColumn = (key) => {
    setSelectedColumns(prev => 
      prev.includes(key) ? prev.filter(c => c !== key) : [...prev, key]
    );
  };

  const imageUrlToDataURL = (url) => fetch(url).then((res) => res.blob()).then((blob) => new Promise((resolve, reject) => {
    const reader = new FileReader(); 
    reader.onloadend = () => resolve(reader.result); 
    reader.onerror = reject; 
    reader.readAsDataURL(blob);
  }));

  // 🔥 UPDATED: Exports ALL data, not just the 30 paginated records
  const exportReport = async (format) => {
    if (totalRecords === 0) return alert("No Data Found to Export!!");
    if (selectedColumns.length === 0) return alert("Please select at least one column to export.");
    
    const userFileName = prompt("Enter a name for your file:", "");
    if (userFileName === null) return;
    const finalFileName = userFileName.trim() || `Students_Export_${new Date().toISOString().split('T')[0]}`;
    
    setExportLoading(true);

    try {
      // 1. Fetch ALL records from Backend (limit: -1 triggers Export All)
      const exportPayload = {
        enr_id: formData.enr_id,
        name: formData.student_name,
        cohort_number: formData.cohort_id,
        batch_id: formData.batch_id,
        gender: formData.gender,
        state_id: formData.state_id,
        district_id: formData.district_id,
        block_id: formData.block_id,
        active_yn: formData.active_yn,
        spl_health_cond: formData.spl_health_cond,
        spl_family_cond: formData.spl_family_cond,
        limit: -1, 
        offset: 0
      };
      
      const params = Object.fromEntries(Object.entries(exportPayload).filter(([_, v]) => v !== "" && v != null));
      const res = await axios.get(`${API_BASE}/api/search-students`, { params });
      const fullDataset = res.data.data || [];

      if (fullDataset.length === 0) {
        alert("No records found to export.");
        return;
      }

      const orderedActiveColumns = selectedColumns.map(key => ALL_COLUMNS.find(c => c.key === key)).filter(Boolean);
      const headers = orderedActiveColumns.map(c => c.label);

      // Map the full unpaginated dataset
      const exportData = fullDataset.map((s, i) => {
        return orderedActiveColumns.map(c => {
          if (c.key === "sl_no") return i + 1;
          if (c.key === "active_yn") return s.active_yn === 'Y' ? "ACTIVE" : "INACTIVE";
          return s[c.key] || "-";
        });
      });

      if (format === "excel") {
        const workbook = XLSX.utils.book_new();
        const ws = XLSX.utils.aoa_to_sheet([headers, ...exportData]);
        XLSX.utils.book_append_sheet(workbook, ws, "Students");
        XLSX.writeFile(workbook, `${finalFileName}.xlsx`);
      }

      if (format === "pdf") {
        const pageFormat = orderedActiveColumns.length > 15 ? "a2" : (orderedActiveColumns.length > 8 ? "a3" : "a4");
        const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: pageFormat });
        const pageWidth = doc.internal.pageSize.getWidth();
        
        let logoDataUrl = null;
        try { logoDataUrl = await imageUrlToDataURL(Logo); } catch (e) { console.warn("Logo could not be loaded"); }
        
        doc.setFontSize(16);
        doc.setFont("helvetica", "bold");
        const titleText = finalFileName.toUpperCase();
        doc.text(titleText, (pageWidth - doc.getTextWidth(titleText)) / 2, 45);
        
        if (logoDataUrl) { doc.addImage(logoDataUrl, "JPEG", pageWidth - 80, 15, 45, 45); }
        
        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        doc.text(`Total Records Exported: ${fullDataset.length} | Generated on: ${new Date().toLocaleDateString('en-GB')}`, 40, 75);
        
        autoTable(doc, {
          startY: 90,
          head: [headers],
          body: exportData,
          theme: "grid",
          headStyles: { fillColor: [16, 185, 129] }, 
          styles: { fontSize: 7, halign: 'center' },
        });
        doc.save(`${finalFileName}.pdf`);
      }
      
      setIsExportModalOpen(false); // Close modal on success
      
    } catch (err) {
      console.error("Export Failed", err);
      alert("Failed to export records. Database error.");
    } finally {
      setExportLoading(false);
    }
  };

  const isQuickSearch = !!formData.enr_id;
  const calculatedTotalPages = totalRecords > 0 ? Math.ceil(totalRecords / itemsPerPage) : 0;
  const canGoNext = calculatedTotalPages > 0 ? currentPage < calculatedTotalPages : results.length === itemsPerPage;

  return (
    <div className="reports-page-container">
      <Breadcrumbs path={currentPath} nonLinkSegments={["Admin", "Academics"]} />
      
      {/* EXPORT CHECKBOX MODAL WITH SEQUENCE & SEARCH */}
      {isExportModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content shadow-lg">
            <div className="modal-header">
              <h2>Select Export Fields & Sequence</h2>
              <button onClick={() => setIsExportModalOpen(false)} className="close-btn" disabled={exportLoading}><X size={20} /></button>
            </div>

            <div className="column-search-wrapper" style={{ marginBottom: '15px' }}>
              <Search className="input-icon" size={16} />
              <input type="text" placeholder="Search fields (e.g. Name, District)..." value={columnSearch} onChange={(e) => setColumnSearch(e.target.value)} className="column-search-input" />
            </div>
            
            <div className="modal-actions">
              <button onClick={() => setSelectedColumns(ALL_COLUMNS.map(c => c.key))} className="btn-text">
                <CheckSquare size={16} /> Select All
              </button>
              <button onClick={() => setSelectedColumns([])} className="btn-text text-red">
                <Square size={16} /> Clear All
              </button>
            </div>

            <div className="checkbox-grid">
              {ALL_COLUMNS.filter(c => c.label.toLowerCase().includes(columnSearch.toLowerCase())).map(col => {
                const seqIndex = selectedColumns.indexOf(col.key);
                return (
                  <label key={col.key} className="checkbox-label" style={{ background: seqIndex !== -1 ? '#f0fdf4' : 'transparent', padding: '6px 8px', borderRadius: '6px' }}>
                    <input type="checkbox" checked={seqIndex !== -1} onChange={() => toggleColumn(col.key)} />
                    <span>{col.label}</span>
                    {seqIndex !== -1 && <span className="sequence-badge">{seqIndex + 1}</span>}
                  </label>
                );
              })}
            </div>

            <div className="modal-footer">
              <button onClick={() => exportReport('pdf')} className="btn-modal-pdf" disabled={exportLoading}>
                {exportLoading ? <Loader2 className="animate-spin" size={16} /> : <FileText size={16} />} 
                {exportLoading ? "Generating..." : "Export All to PDF"}
              </button>
              <button onClick={() => exportReport('excel')} className="btn-modal-excel" disabled={exportLoading}>
                {exportLoading ? <Loader2 className="animate-spin" size={16} /> : <Package size={16} />} 
                {exportLoading ? "Generating..." : "Export All to Excel"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="container" style={{ marginTop: '20px' }}>
        <div className="page-header" style={{ marginBottom: '15px' }}>
          <div>
            <h1 className="title" style={{ fontSize: '24px', fontWeight: 'bold', color: '#1e293b', margin: 0 }}>STUDENT PROFILE</h1>
            <p className="subtitle" style={{ color: '#64748b', margin: '4px 0 8px 0' }}>Filter, view, and export student records</p>
            {/* 🔥 TOTAL RECORDS SHOWN TOP LEFT */}
            {totalRecords > 0 && (
              <span style={{ background: '#e0f2fe', color: '#0369a1', padding: '4px 12px', borderRadius: '12px', fontWeight: 'bold', fontSize: '13px', display: 'inline-block' }}>
                Total Records Found: {totalRecords}
              </span>
            )}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="shadow-md rounded-lg bg-white" style={{ padding: '24px', marginBottom: '20px' }}>
          <div className="filter-bar" style={{ marginBottom: '20px' }}>
            <div className="filter-item" style={{ flex: 1 }}>
              <Search className="input-icon" />
              <input name="enr_id" placeholder="Quick Search by Enrollment ID..." value={formData.enr_id} onChange={(e) => setFormData({ ...formData, enr_id: e.target.value })} />
            </div>
            <div className="filter-item" style={{ flex: 1 }}>
              <Users className="input-icon" />
              <input name="student_name" placeholder="Search by Student Name..." value={formData.student_name} onChange={(e) => setFormData({ ...formData, student_name: e.target.value })} disabled={isQuickSearch} />
            </div>
          </div>

          <div style={{ borderTop: '1px solid #e2e8f0', margin: '20px 0' }}></div>

          <fieldset disabled={isQuickSearch} style={{ border: 'none', padding: 0, margin: 0 }}>
            <div className="filter-bar">
              <div className="filter-item">
                <Layers className="input-icon" />
                <select value={formData.cohort_id} onChange={(e) => setFormData({ ...formData, cohort_id: e.target.value, batch_id: "" })}>
                  <option value="">All Cohorts</option>
                  {cohorts.map(c => <option key={c.cohort_number} value={c.cohort_number}>{c.cohort_name}</option>)}
                </select>
              </div>
              <div className="filter-item">
                <Users className="input-icon" />
                <select value={formData.batch_id} onChange={(e) => setFormData({ ...formData, batch_id: e.target.value })} disabled={!formData.cohort_id}>
                  <option value="">All Batches</option>
                  {batches.map(b => <option key={b.batch_id} value={b.batch_id}>{b.batch_name}</option>)}
                </select>
              </div>
              <div className="filter-item">
                <MapPin className="input-icon" />
                <select value={formData.state_id} onChange={(e) => setFormData({ ...formData, state_id: e.target.value, district_id: "", block_id: "" })}>
                  <option value="">All States</option>
                  {states.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div className="filter-item">
                <MapPin className="input-icon" />
                <select value={formData.district_id} onChange={(e) => setFormData({ ...formData, district_id: e.target.value, block_id: "" })} disabled={!formData.state_id}>
                  <option value="">All Districts</option>
                  {districts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>
              <div className="filter-item">
                <MapPin className="input-icon" />
                <select value={formData.block_id} onChange={(e) => setFormData({ ...formData, block_id: e.target.value })} disabled={!formData.district_id}>
                  <option value="">All Blocks</option>
                  {blocks.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
              
              <div className="filter-item">
                <UserCheck className="input-icon" />
                <select value={formData.gender} onChange={(e) => setFormData({ ...formData, gender: e.target.value })}>
                  <option value="">Gender (All)</option>
                  <option value="M">Male</option>
                  <option value="F">Female</option>
                  <option value="O">Other</option>
                </select>
              </div>
              <div className="filter-item">
                <UserCheck className="input-icon" />
                <select value={formData.active_yn} onChange={(e) => setFormData({ ...formData, active_yn: e.target.value })}>
                  <option value="">Status (All)</option>
                  <option value="Y">Active Only</option>
                  <option value="N">Inactive Only</option>
                </select>
              </div>
              <div className="filter-item">
                <Activity className="input-icon" />
                <select value={formData.spl_health_cond} onChange={(e) => setFormData({ ...formData, spl_health_cond: e.target.value })}>
                  <option value="">Spl Health Cond (Any)</option>
                  <option value="Y">Yes</option>
                  <option value="N">No</option>
                </select>
              </div>
              <div className="filter-item">
                <HeartHandshake className="input-icon" />
                <select value={formData.spl_family_cond} onChange={(e) => setFormData({ ...formData, spl_family_cond: e.target.value })}>
                  <option value="">Spl Family Cond (Any)</option>
                  <option value="Y">Yes</option>
                  <option value="N">No</option>
                </select>
              </div>
            </div>
          </fieldset>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '16px', marginTop: '24px' }}>
            <button type="button" onClick={handleReset} className="btn-search-reset">
              <RotateCcw size={16} /> Reset
            </button>
            <button type="submit" disabled={loading} className="btn-search-submit">
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />} 
              {loading ? "Searching..." : "Search"}
            </button>
            
            {/* 🔥 CUSTOM EXPORT MOVED BESIDE SEARCH */}
            <button 
              type="button"
              className="btn-custom-export" 
              onClick={() => setIsExportModalOpen(true)} 
              disabled={results.length === 0}
            >
              <Download size={18} /> Export All
            </button>
          </div>
        </form>

        <div className="shadow-md rounded-lg bg-white" style={{ padding: '20px' }}>
          {results.length === 0 && !loading ? (
            <div className="text-center py-10" style={{ color: '#94a3b8', padding: '40px 0' }}>
              <Users size={48} style={{ opacity: 0.2, margin: '0 auto 15px auto' }} />
              <p>No students found. Adjust your filters and click Search.</p>
            </div>
          ) : (
            <>
              {/* TOP SCROLLBAR HACK */}
              <div className="top-scrollbar-wrapper" style={{ overflowX: 'auto', marginBottom: '10px', transform: 'rotateX(180deg)' }}>
                <div className="top-scrollbar-inner" style={{ minWidth: 'max-content', transform: 'rotateX(180deg)' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', whiteSpace: 'nowrap' }}>
                    <thead>
                      <tr>
                        {ALL_COLUMNS.map(col => (
                          <th key={col.key} style={{ background: '#f8fafc', padding: '12px', border: '1px solid #e2e8f0', textAlign: 'center', fontSize: '13px', color: '#334155' }}>
                            {col.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {results.map((s, idx) => (
                        <tr key={s.applicant_id || idx} style={{ transition: 'background 0.2s', ':hover': { background: '#f1f5f9' } }}>
                          {ALL_COLUMNS.map(col => {
                            let displayValue = s[col.key] || "-";
                            if (col.key === 'sl_no') displayValue = ((currentPage - 1) * itemsPerPage) + idx + 1;
                            if (col.key === 'active_yn') displayValue = s.active_yn === 'Y' ? "ACTIVE" : "INACTIVE";
                            
                            if (col.key === 'student_name') {
                              return (
                                <td key={col.key} style={{ padding: '12px 10px', border: '1px solid #e2e8f0', fontWeight: '600', fontSize: '13px' }}>
                                  <Link to={`/admin/academics/students/view-student-info/${s.nmms_reg_number}`} style={{ color: '#0369a1', textDecoration: 'none' }}>
                                    {displayValue}
                                  </Link>
                                </td>
                              );
                            }

                            if (col.key === 'active_yn') {
                              return (
                                <td key={col.key} style={{ padding: '12px 10px', border: '1px solid #e2e8f0', textAlign: 'center', fontSize: '13px' }}>
                                   <span style={{ padding: '4px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', background: s.active_yn === 'Y' ? '#dcfce7' : '#fee2e2', color: s.active_yn === 'Y' ? '#166534' : '#991b1b' }}>
                                    {displayValue}
                                  </span>
                                </td>
                              );
                            }

                            return (
                              <td key={col.key} style={{ padding: '12px 10px', border: '1px solid #e2e8f0', textAlign: 'center', fontSize: '13px', color: '#475569' }}>
                                {displayValue}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {results.length > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '15px', borderTop: '1px solid #e2e8f0' }}>
                  <span style={{ fontSize: '14px', color: '#475569', fontWeight: '500' }}>
                    Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalRecords > 0 ? totalRecords : (currentPage * itemsPerPage))} {totalRecords > 0 ? `of ${totalRecords} entries` : ""}
                  </span>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="btn-pagination">
                      Previous
                    </button>
                    <button onClick={() => handlePageChange(currentPage + 1)} disabled={!canGoNext} className="btn-pagination">
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      
      <style>{`
        .reports-page-container { background: #f8fafc; min-height: 100vh; padding-bottom: 50px; font-family: system-ui, -apple-system, sans-serif; }
        .filter-bar { display: flex; gap: 16px; flex-wrap: wrap; }
        .filter-item { position: relative; flex: 1; min-width: 200px; }
        .filter-item select, .filter-item input { width: 100%; padding: 12px 12px 12px 38px; border: 1px solid #cbd5e1; border-radius: 6px; font-size: 14px; box-sizing: border-box; background: white; outline: none; transition: border-color 0.2s, box-shadow 0.2s; color: #334155; }
        .filter-item select:focus, .filter-item input:focus { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,0.1); }
        .filter-item select:disabled, .filter-item input:disabled { background: #f1f5f9; cursor: not-allowed; color: #94a3b8; }
        .input-icon { position: absolute; left: 14px; top: 14px; width: 16px; height: 16px; color: #64748b; pointer-events: none; }
        
        .animate-spin { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        
        .modal-overlay { position: fixed; top: 0; left: 0; width: 100vw; height: 100vh; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 9999; }
        .modal-content { background: white; width: 90%; max-width: 900px; border-radius: 12px; padding: 24px; max-height: 90vh; overflow-y: auto; }
        .modal-header { display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid #e2e8f0; padding-bottom: 12px; margin-bottom: 15px; }
        .modal-header h2 { margin: 0; font-size: 18px; color: #1e293b; font-weight: 700; }
        .close-btn { background: none; border: none; cursor: pointer; color: #64748b; }
        .close-btn:hover { color: #ef4444; }
        
        .column-search-wrapper { position: relative; width: 100%; }
        .column-search-input { width: 100%; padding: 10px 10px 10px 36px; border: 1px solid #cbd5e1; border-radius: 6px; font-size: 14px; box-sizing: border-box; outline: none; }
        .column-search-input:focus { border-color: #2563eb; }

        .modal-actions { display: flex; gap: 15px; margin-bottom: 15px; }
        .btn-text { background: none; border: none; display: flex; align-items: center; gap: 5px; cursor: pointer; font-size: 13px; font-weight: 600; color: #3b82f6; }
        .btn-text.text-red { color: #ef4444; }
        
        .checkbox-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(230px, 1fr)); gap: 12px; margin-bottom: 24px; }
        .checkbox-label { display: flex; align-items: center; gap: 8px; font-size: 13px; color: #334155; cursor: pointer; transition: background 0.2s; }
        .checkbox-label input { width: 16px; height: 16px; cursor: pointer; flex-shrink: 0; }
        .sequence-badge { background: #10b981; color: white; border-radius: 50%; width: 20px; height: 20px; display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: bold; margin-left: auto; flex-shrink: 0; }

        .sequence-preview { background: #f8fafc; border: 1px solid #e2e8f0; padding: 12px; border-radius: 8px; margin-bottom: 20px; font-size: 12px; color: #475569; line-height: 1.5; }
        
        .modal-footer { display: flex; justify-content: flex-end; gap: 12px; border-top: 1px solid #e2e8f0; padding-top: 16px; }

        .btn-custom-export { display: flex; align-items: center; gap: 8px; padding: 10px 16px; background-color: #10b981 !important; color: white !important; border: none !important; border-radius: 6px; cursor: pointer; font-weight: 600; transition: background-color 0.2s; }
        .btn-custom-export:hover:not(:disabled) { background-color: #059669 !important; }
        .btn-custom-export:disabled { background-color: #94a3b8 !important; cursor: not-allowed; opacity: 0.6; }
        
        .btn-modal-pdf { display: flex; align-items: center; gap: 8px; padding: 10px 20px; background-color: #ef4444 !important; color: white !important; border: none !important; border-radius: 6px; cursor: pointer; font-weight: 600; transition: background-color 0.2s; }
        .btn-modal-pdf:hover:not(:disabled) { background-color: #dc2626 !important; }
        
        .btn-modal-excel { display: flex; align-items: center; gap: 8px; padding: 10px 20px; background-color: #10b981 !important; color: white !important; border: none !important; border-radius: 6px; cursor: pointer; font-weight: 600; transition: background-color 0.2s; }
        .btn-modal-excel:hover:not(:disabled) { background-color: #059669 !important; }
        
        .btn-search-reset { display: flex; align-items: center; gap: 8px; padding: 10px 20px; background-color: transparent !important; color: #334155 !important; border: 1px solid #cbd5e1 !important; border-radius: 6px; cursor: pointer; font-weight: 600; transition: all 0.2s; }
        .btn-search-reset:hover { background-color: #f1f5f9 !important; border-color: #94a3b8 !important; }
        
        .btn-search-submit { display: flex; align-items: center; gap: 8px; padding: 10px 24px; background-color: #2563eb !important; color: white !important; border: none !important; border-radius: 6px; cursor: pointer; font-weight: 600; transition: background-color 0.2s; }
        .btn-search-submit:hover:not(:disabled) { background-color: #1d4ed8 !important; }
        .btn-search-submit:disabled { background-color: #94a3b8 !important; cursor: not-allowed; }

        .btn-pagination { padding: 8px 16px; background-color: white !important; color: #334155 !important; border: 1px solid #cbd5e1 !important; border-radius: 6px; cursor: pointer; font-weight: 600; font-size: 13px; transition: all 0.2s; }
        .btn-pagination:hover:not(:disabled) { background-color: #f8fafc !important; border-color: #94a3b8 !important; }
        .btn-pagination:disabled { opacity: 0.5; cursor: not-allowed; background-color: #f8fafc !important; color: #94a3b8 !important; }
      `}</style>
    </div>
  );
}





